// Persona-based onboarding config.
//
// The server is the source of truth for a client user. This slice caches one
// identity's configs for the life of the tab and reaches localStorage for
// none of them. The config arrives in the `/user_preferences` GET the app
// root issues once per load, so a localStorage copy would be a second source
// of truth that could disagree with Firestore and mask a broken read.
//
// A Dashmote staff member's answers are the exception and live in
// `staffSession`, mirrored to sessionStorage so they last the session and no
// longer. `utils/staffPersonaSession.ts` carries the reasoning.
//
// State is keyed by uid, then by WORKSPACE: each workspace the user enters gets
// its own setup, because market assignments and franchise/brand universes are
// workspace-shaped.
//
// The uid keying is load-bearing under impersonation, not merely tidy.
// "View as User" swaps the Firebase identity (authSlice's
// setImpersonationActive, after signInWithCustomToken), so `auth.user.uid`
// becomes the target's — but no second fetch runs, because App.jsx guards it
// with a once-per-mount ref. This slice therefore still holds the ADMIN's
// configs. Stamping the hydration with the uid it was fetched FOR is what
// makes the target's uid resolve to nothing and fall back to the
// everything-view, instead of showing an admin their own persona while they
// believe they are seeing the client's.
//
// Do not read or dispatch this slice directly from components. The single
// owner module is `hooks/usePersonaConfig.ts`.

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { fetchUserPreferencesFromBackend } from "./userPreferencesSlice";
import type {
  ExtraKind,
  PersonaConfig,
  PersonaId,
} from "../data/personaConfigTypes";
import {
  EMPTY_STAFF_PERSONA_SESSION,
  type StaffPersonaSession,
} from "../utils/staffPersonaSession";

export interface WorkspacePersonaConfigs {
  byWorkspace: Record<string, PersonaConfig>;
}

// How far the read of the server has got. Gate 5 turns the ABSENCE of a config
// into an onboarding redirect, so it must be able to tell "no config" from
// "not asked yet". Collapsing the two bounces an onboarded user into the
// wizard on every load, in the frames before the fetch returns.
export type PersonaLoadStatus = "idle" | "loading" | "loaded" | "error";

export interface PersonaConfigState {
  configsByUid: Record<string, WorkspacePersonaConfigs>;
  status: PersonaLoadStatus;
  // The uid the current `configsByUid` was fetched for, so a hydration cannot
  // be read as belonging to a different identity.
  hydratedForUid: string | null;
  // Dashmote staff answers, which the server never sees
  // (`utils/staffPersonaSession.ts` says why). This is a field of its own
  // rather than an entry in `configsByUid`, because the `/user_preferences`
  // hydration below replaces that map wholesale: staff answers put there
  // would be erased by the next read, and a server row left over from an
  // earlier build would take their place.
  staffSession: StaffPersonaSession;
}

const initialState: PersonaConfigState = {
  configsByUid: {},
  status: "idle",
  hydratedForUid: null,
  staffSession: EMPTY_STAFF_PERSONA_SESSION,
};

// The server stores `persona_config.{id_client}` with doc-level snake_case
// keys. The client type is camelCase, so this is the one translation point.
// The `Selection` union's `{ kind, ids }` passes through untouched by design:
// it is opaque to both sides, so neither needs to know the other's shape.
const fromServer = (stored: Record<string, unknown>): PersonaConfig | null => {
  const persona = stored.persona;
  const markets = stored.markets;
  if (typeof persona !== "string") return null;
  return {
    schemaVersion: stored.schema_version as PersonaConfig["schemaVersion"],
    persona: persona as PersonaId,
    premise: stored.premise as PersonaConfig["premise"],
    focus: stored.focus as PersonaConfig["focus"],
    markets: (markets ?? {}) as PersonaConfig["markets"],
    completedAt: (stored.completed_at as string) ?? "",
    extras: stored.extras as PersonaConfig["extras"],
  };
};

// The add/remove/dedupe rule for "Add to home", owned here because two
// callers apply it: this slice's reducer for the optimistic update, and
// usePersonaConfig for the body it PUTs. Written twice, a change to either
// would silently diverge what the user sees from what is stored.
export const nextExtras = (
  extras: PersonaConfig["extras"],
  kind: ExtraKind,
  id: string,
  on: boolean,
): PersonaConfig["extras"] => {
  const current = extras?.[kind] ?? [];
  const next = on
    ? current.includes(id)
      ? current
      : [...current, id]
    : current.filter((x) => x !== id);
  return { ...extras, [kind]: next };
};

const personaConfigSlice = createSlice({
  name: "personaConfig",
  initialState,
  reducers: {
    setPersonaConfig: (
      state,
      action: PayloadAction<{
        uid: string;
        workspaceId: string;
        config: PersonaConfig;
      }>,
    ) => {
      const { uid, workspaceId, config } = action.payload;
      if (!uid || !workspaceId) return;
      const entry = state.configsByUid[uid] ?? { byWorkspace: {} };
      entry.byWorkspace[workspaceId] = config;
      state.configsByUid[uid] = entry;
    },
    // Home Show all → "Add to home": widen the user's own view with an
    // entitled item outside their persona's set. `on` is explicit so the
    // action is observable by trackingMiddleware without re-deriving state.
    toggleExtra: (
      state,
      action: PayloadAction<{
        uid: string;
        workspaceId: string;
        kind: ExtraKind;
        id: string;
        on: boolean;
      }>,
    ) => {
      const { uid, workspaceId, kind, id, on } = action.payload;
      const config = state.configsByUid[uid]?.byWorkspace[workspaceId];
      if (!config) return;
      config.extras = nextExtras(config.extras, kind, id, on);
    },
    // The whole staff session at once — hydrating it from sessionStorage and
    // recording an answer are the same write. `usePersonaConfig` builds the
    // next map and mirrors the identical object to sessionStorage, so the
    // two copies cannot describe different things.
    setStaffSession: (state, action: PayloadAction<StaffPersonaSession>) => {
      state.staffSession = action.payload;
    },
    // No state change — exists so trackingMiddleware can observe Show-all
    // usage (the success metric for whether a persona's set is right).
    showAllToggled: (
      _state,
      _action: PayloadAction<{
        list: "dashboards" | "workflows";
        on: boolean;
        persona: PersonaId | null;
        surface: "sidebar" | "home";
      }>,
    ) => {},
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserPreferencesFromBackend.pending, (state) => {
        if (state.status === "idle") state.status = "loading";
      })
      .addCase(fetchUserPreferencesFromBackend.fulfilled, (state, action) => {
        // Hydrate on the FIRST successful fetch only. Three page-level hooks
        // re-dispatch this thunk, and a later response would overwrite an edit
        // whose PUT has not landed yet. For a user part-way through the
        // wizard it would wipe the in-progress config with the empty map the
        // server correctly returns. A re-fetch therefore leaves persona state
        // alone.
        if (state.status === "loaded") return;
        const { data, uid } = action.payload ?? {};
        const stored = data?.persona_config;
        const byWorkspace: Record<string, PersonaConfig> = {};
        if (uid && stored && typeof stored === "object") {
          for (const [workspaceId, config] of Object.entries(stored)) {
            if (!config || typeof config !== "object") continue;
            const parsed = fromServer(config as Record<string, unknown>);
            if (parsed) byWorkspace[workspaceId] = parsed;
          }
        }
        state.configsByUid = uid ? { [uid]: { byWorkspace } } : {};
        state.hydratedForUid = uid;
        state.status = "loaded";
      })
      .addCase(fetchUserPreferencesFromBackend.rejected, (state) => {
        // A failed read is NOT an absent config. Resolving it as absence would
        // let a network blip send an onboarded user through the wizard, whose
        // completion overwrites the config they already had. Gate 5 reads this
        // status and renders the everything-view instead.
        if (state.status !== "loaded") state.status = "error";
      });
  },
});

export const {
  setPersonaConfig,
  toggleExtra,
  setStaffSession,
  showAllToggled,
} = personaConfigSlice.actions;
export default personaConfigSlice.reducer;
